/**
 * src/components/spatial-bindings/objectCandidates.ts
 * Enumerating the spatial objects a binding UI can offer.
 *
 * Why this is not one call to GeoLink with `country_code`: that column is
 * populated per deployment and per object, and GeoLink applies the filter
 * in SQL. An object whose country was never backfilled matches nothing, so
 * a country-scoped query returns an empty list that looks exactly like
 * "this country has no objects". That was the state of every object,
 * Burj Khalifa included, until the backfill described below.
 *
 * An empty answer that is indistinguishable from a correct one is the
 * worst failure mode available here, so the filter is treated as an
 * optimisation: use it when it returns something, fall back to the whole
 * list when it does not, and tell the caller which happened so the UI can
 * say the list is not country-scoped.
 *
 * `scripts/backfill-object-countries.js` populates the column by
 * point-in-polygon against GeoTrust's own borders, which is what stops the
 * fallback firing in practice. The fallback stays regardless: an object
 * created by a client that does not send `country_code` reintroduces the
 * gap at any time, and it is still better to show an unscoped list with a
 * warning than an empty one without.
 */

import {
  geoLinkSpatialObjectsAdminClient,
  type SpatialObjectAdminRow,
} from '../../services/spatial/geoLinkSpatialObjectsAdminClient';
import type { SpatialObjectRecord } from '../../v2/services/spatialRegistryService';

/** Upper bound on rows pulled for a picker or an organization's list. */
const CANDIDATE_LIMIT = 500;

export interface ObjectCandidates {
  rows: SpatialObjectAdminRow[];
  /** True when the rows really are scoped to the requested country. */
  countryScoped: boolean;
  /** Set when the country filter was tried and returned nothing usable. */
  countryDataIncomplete: boolean;
}

export async function listCandidateObjects(alpha2: string | null): Promise<ObjectCandidates> {
  if (!alpha2) {
    const rows = await geoLinkSpatialObjectsAdminClient.listObjects({ limit: CANDIDATE_LIMIT });
    return { rows, countryScoped: false, countryDataIncomplete: false };
  }

  const scoped = await geoLinkSpatialObjectsAdminClient.listObjects({
    limit: CANDIDATE_LIMIT,
    country_code: alpha2,
  });
  if (scoped.length) return { rows: scoped, countryScoped: true, countryDataIncomplete: false };

  const all = await geoLinkSpatialObjectsAdminClient.listObjects({ limit: CANDIDATE_LIMIT });
  return {
    rows: all,
    countryScoped: false,
    // Only worth reporting when there were objects to miss.
    countryDataIncomplete: all.length > 0,
  };
}

/**
 * The organization GeoLink attributes an object to.
 *
 * Reads the metadata key first and the promoted column second: the column
 * is only populated once GeoLink has mirrored the organization, so a
 * deployment that has not promoted yet still answers correctly.
 */
export function readMirrorOrgId(row: SpatialObjectAdminRow): string | null {
  const meta = (row.metadata as { organization_id?: unknown } | null | undefined)?.organization_id;
  const fromMeta = typeof meta === 'string' ? meta.trim().toLowerCase() : '';
  const fromColumn = (row.organizationId || '').trim().toLowerCase();
  return fromMeta || fromColumn || null;
}

/** The organization the chain says an object answers to. */
export function readChainOrgId(record: SpatialObjectRecord | null | undefined): string | null {
  const id = (record?.binding?.organizationId || record?.effectiveOrganizationId || '')
    .trim()
    .toLowerCase();
  return id || null;
}

/**
 * How many candidates get verified against the chain.
 *
 * Not free: the backend runs about nine simulations per object and caps a
 * batch at 50, so this is 4 requests at full stretch. Backfilling
 * `country_code` is what usually keeps the real number far below the cap,
 * since a country-scoped candidate list is a handful of objects rather
 * than every object GeoLink holds.
 */
export const CHAIN_VERIFY_LIMIT = 200;

export interface OrganizationObjectSelection<Row> {
  /** Candidates worth reading from the chain, mirror matches first. */
  toVerify: Row[];
  /** True when the candidate set outgrew the cap, so the result may be short. */
  truncated: boolean;
}

/**
 * Order candidates for chain verification, and say when the cap bit.
 *
 * Mirror matches go first because they are members whether or not the
 * chain read succeeds, so they must never be the rows the cap discards.
 * Everything else follows, and is a member only if the chain says so.
 */
export function orderCandidatesForVerification<Row>(
  rows: Row[],
  mirrorOrgIdOf: (row: Row) => string | null,
  orgId: string,
  cap: number = CHAIN_VERIFY_LIMIT,
): OrganizationObjectSelection<Row> {
  const mirrorMatched = rows.filter((r) => mirrorOrgIdOf(r) === orgId);
  const rest = rows.filter((r) => mirrorOrgIdOf(r) !== orgId);
  const toVerify = [...mirrorMatched, ...rest].slice(0, cap);
  return { toVerify, truncated: rows.length > toVerify.length };
}

/**
 * Whether an object belongs in an organization's list.
 *
 * The union of what the chain says and what GeoLink says, and the order of
 * that sentence is the whole point. Filtering on the mirror alone is what
 * made a bound object invisible: Burj Khalifa was approved on chain while
 * Emaar's list read "none", because the mirror write had never landed and
 * the row was dropped before anything compared the two.
 *
 * The GeoLink half stays because an object GeoLink attributes here with no
 * chain binding behind it is exactly a row that needs binding.
 */
export function belongsToOrganization(
  chainOrgId: string | null,
  mirrorOrgId: string | null,
  orgId: string,
): boolean {
  return chainOrgId === orgId || mirrorOrgId === orgId;
}
